import { Box, Button, Pagination, Stack, Text } from '@ltvco/refresh-lib/theme';

import { SortByDropdown, type SortOption } from '../../../shared';
import { RecallsCardsGrid } from '../components';
import { RecallCard } from '../RecallCard';

import type { NormalizedRecallCard, ResultsPayload } from '../../interfaces';
import type { FC } from 'react';

export interface RecallsResultsViewProps {
  activeResults: ResultsPayload;
  pageCards: NormalizedRecallCard[];
  currentPage: number;
  totalPages: number;
  showPagination: boolean;
  recallsPerPage: number;
  sortOption: string;
  sortOptions: SortOption[];
  onSortChange: (value: string) => void;
  onBack: () => void;
  onCardClick: (card: NormalizedRecallCard) => void;
  onPageChange: (page: number) => void;
}

export const RecallsResultsView: FC<RecallsResultsViewProps> = ({
  activeResults,
  pageCards,
  currentPage,
  totalPages,
  showPagination,
  sortOption,
  sortOptions,
  onSortChange,
  onBack,
  onCardClick,
  onPageChange,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Stack sx={{ mb: 2 }}>
        <Text variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
          We found{' '}
          <Text component="span" fontWeight={700} color="text.secondary">
            {activeResults.count}
          </Text>{' '}
          {activeResults.recallType} recall
          {activeResults.count !== 1 ? 's' : ''} for:
        </Text>
        <Stack spacing={1.5} sx={{ m: 0, mb: 1 }}>
          <Stack
            direction="row"
            alignItems="baseline"
            flexWrap="wrap"
            gap={1}
            sx={(theme) => ({
              m: 0,
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                alignItems: 'flex-start',
              },
            })}
          >
            <Text
              component="h3"
              sx={{
                fontWeight: 700,
                fontSize: 24,
                m: 0,
              }}
            >
              {activeResults.title}
            </Text>
            <Button
              variant="text"
              color="primary"
              onClick={onBack}
              sx={(theme) => ({
                textTransform: 'none',
                minWidth: 0,
                px: theme.spacing(1.25),
              })}
            >
              Clear Search
            </Button>
          </Stack>
          <SortByDropdown
            value={sortOption}
            options={sortOptions}
            onChange={onSortChange}
            label="Sort By"
            id="recalls-sort-by-dropdown"
            defaultValue="date_desc"
          />
        </Stack>
      </Stack>
      <RecallsCardsGrid>
        {pageCards.map((card, index) => (
          <RecallCard
            key={card.recallNumber ?? `${card.title}-${index}`}
            card={card}
            onClick={() => onCardClick(card)}
          />
        ))}
      </RecallsCardsGrid>
      {showPagination && totalPages > 1 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
          <Box
            component="nav"
            aria-label="Recalls results pagination"
            sx={{
              '& .MuiPagination-ul': { justifyContent: 'center' },
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_: unknown, page: number) => onPageChange(page)}
              color="primary"
              shape="circular"
              sx={{
                '& .MuiPaginationItem-root.Mui-selected': {
                  backgroundColor: 'primary.main',
                  borderColor: 'primary.main',
                  color: 'primary.contrastText',
                },
              }}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
};
